/**
 * useGoalOperations — Core CRUD operations for goals (create, update, delete, reorder).
 */
import { Goal } from "@/types/todo";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

export const useGoalOperations = (invalidateGoals: () => Promise<void>, userId: string | undefined) => {
  const { toast } = useToast();

  const addGoal = async (goal: Goal) => {
    try {
      let categoryId: string | null = null;
      
      if (goal.category) {
        const { data: categoryData } = await supabase
          .from('categories')
          .select('id')
          .eq('name', goal.category)
          .single();

        categoryId = categoryData?.id || null;
      }

      const { error } = await supabase
        .from('goals')
        .insert([{
          title: goal.title,
          category_id: categoryId,
          due_date: goal.dueDate ? goal.dueDate : null,
          progress: 0,
          user_id: userId
        }]);

      if (error) throw error;
      
      await invalidateGoals();
      
      // Scroll to the top of the goals list so the user sees their new goal
      if (typeof window !== 'undefined') {
        requestAnimationFrame(() => {
          const el = document.getElementById('goals-list-top');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }

      toast({
        title: "Goal created",
        description: "Your new goal has been added to the top of your list.",
      });
    } catch (error: any) {
      toast({
        title: "Error creating goal",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteGoal = async (goalToDelete: Goal) => {
    try {
      logger.debug("[GoalOps] Starting safe deletion for goal:", goalToDelete.title);
      
      const { data: goalRecord } = await supabase
        .from('goals')
        .select('common_goal_id')
        .eq('id', goalToDelete.id)
        .single();
      
      const commonGoalId = goalRecord?.common_goal_id || goalToDelete.commonGoalId;
      
      const { data: todos } = await supabase
        .from('todos')
        .select('id')
        .eq('goal_id', goalToDelete.id);
      
      const todoIds = todos?.map(t => t.id) || [];
      logger.debug("[GoalOps] Found", todoIds.length, "todos to check for pledges");
      
      const { error: unlinkGoalError } = await supabase
        .from('reward_pledges')
        .update({ goal_id: null })
        .eq('goal_id', goalToDelete.id);
      
      if (unlinkGoalError) {
        logger.error("[GoalOps] Error unlinking pledges from goal:", unlinkGoalError);
        throw new Error("Could not unlink reward pledges from goal. Please try again.");
      }
      
      if (todoIds.length > 0) {
        const { error: unlinkTodoError } = await supabase
          .from('reward_pledges')
          .update({ todo_id: null })
          .in('todo_id', todoIds);
        
        if (unlinkTodoError) {
          logger.error("[GoalOps] Error unlinking pledges from todos:", unlinkTodoError);
          throw new Error("Could not unlink reward pledges from todos. Please try again.");
        }
      }
      
      if (commonGoalId && userId) {
        logger.debug("[GoalOps] Removing community participation for common_goal_id:", commonGoalId);
        const { error: participationError } = await supabase
          .from('common_goal_participants')
          .delete()
          .eq('user_id', userId)
          .eq('common_goal_id', commonGoalId);
        
        if (participationError) {
          logger.warn("[GoalOps] Could not remove community participation (non-blocking):", participationError);
        }
      }
      
      const { error } = await supabase
        .from('goals')
        .delete()
        .eq('id', goalToDelete.id)
        .eq('user_id', userId);

      if (error) throw error;
      
      await invalidateGoals();
      
      toast({
        title: "Goal deleted",
        description: "Your goal has been deleted. Linked rewards were preserved.",
      });
    } catch (error: any) {
      logger.error("[GoalOps] Error in deleteGoal:", error);
      toast({
        title: "Error deleting goal",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateGoal = async (updatedGoal: Goal) => {
    try {
      logger.debug("[GoalOps] Updating goal:", updatedGoal.title);
      
      let categoryId: string | null = null;
      
      if (updatedGoal.category) {
        const { data: categoryData } = await supabase
          .from('categories')
          .select('id')
          .eq('name', updatedGoal.category)
          .single();

        categoryId = categoryData?.id || null;
      }

      const safeProgress = typeof updatedGoal.progress === 'number' && !isNaN(updatedGoal.progress)
        ? Math.max(0, Math.min(100, Math.round(updatedGoal.progress)))
        : 0;

      const { error } = await supabase
        .from('goals')
        .update({
          title: updatedGoal.title,
          category_id: categoryId,
          due_date: updatedGoal.dueDate ? updatedGoal.dueDate : null,
          progress: safeProgress,
          completed_at: updatedGoal.completedAt,
          celebrated_at: updatedGoal.celebratedAt,
        })
        .eq('id', updatedGoal.id)
        .eq('user_id', userId);

      if (error) throw error;
      
      await invalidateGoals();
      
      toast({
        title: "Goal updated",
        description: "Your goal has been updated successfully.",
      });
    } catch (error: any) {
      logger.error("[GoalOps] Error updating goal:", error);
      toast({
        title: "Error updating goal",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return {
    addGoal,
    deleteGoal,
    updateGoal
  };
};
